import type {
  AliasCandidate,
  BrandResearchResult,
  DiscoveredCompany,
  KeywordResearchResult,
} from "@crux/contracts";
import { z } from "zod";
import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * The research tools: questions, keywords, the brand itself, and the
 * companies an answer named.
 */

/**
 * The server's own sentence when it refused the request (400) or asked for a
 * pause (429): "That does not look like a website." says what to change, where
 * "Could not research this brand (400)" only says that something did not work.
 */
function refusal(status: number, error: unknown): string | null {
  if (status !== 400 && status !== 429) return null;
  const parsed = refusalSchema.safeParse(error);
  return parsed.success && parsed.data.message !== "" ? parsed.data.message : null;
}

const refusalSchema = z.object({ message: z.string() });

type KeywordResearchBody = NonNullable<
  paths["/api/workspaces/{workspaceId}/research/keywords"]["post"]["requestBody"]
>["content"]["application/json"];

export async function researchKeywords(
  workspaceId: string,
  body: KeywordResearchBody,
): Promise<KeywordResearchResult | "unavailable"> {
  const response = await api.POST("/api/workspaces/{workspaceId}/research/keywords", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    if (response.response.status === 501) return "unavailable";
    throw new Error(
      refusal(response.response.status, response.error) ??
        problem(response.response.status, "turn keywords into questions"),
    );
  }
  return response.data;
}

type BrandResearchBody = NonNullable<
  paths["/api/workspaces/{workspaceId}/research/brand"]["post"]["requestBody"]
>["content"]["application/json"];

export async function researchBrand(
  workspaceId: string,
  body: BrandResearchBody,
): Promise<BrandResearchResult | "unavailable"> {
  const response = await api.POST("/api/workspaces/{workspaceId}/research/brand", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    if (response.response.status === 501) return "unavailable";
    throw new Error(
      refusal(response.response.status, response.error) ??
        problem(response.response.status, "research this brand"),
    );
  }
  return response.data;
}

export async function fetchDiscoveredCompanies(
  workspaceId: string,
  reportId: string,
): Promise<{ companies: DiscoveredCompany[]; verified: boolean }> {
  const response = await api.GET("/api/workspaces/{workspaceId}/discovered-companies", {
    params: { path: { workspaceId }, query: { reportId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load discovered companies"));
  }
  return { companies: [...response.data.companies], verified: response.data.verified };
}

export async function fetchAliasCandidates(workspaceId: string): Promise<{
  candidates: AliasCandidate[];
  model: string | null;
}> {
  const response = await api.GET("/api/workspaces/{workspaceId}/alias-candidates", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load alias candidates"));
  }
  return { candidates: [...response.data.candidates], model: response.data.model };
}

export async function approveAlias(
  workspaceId: string,
  input: { readonly brandId: string; readonly alias: string },
): Promise<{ linked: number }> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/alias-candidates/approve",
    { params: { path: { workspaceId } }, body: input },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "approve alias"));
  }
  return { linked: response.data.linked };
}
